/**
 * JobStepsModal. A centered, in-place overlay that shows a monitored job's
 * logic as a numbered step list (cockpit_ops_jobs.detail_steps, seeded by api
 * migration 0058). Same look as the shared NotesModal (backdrop + centered
 * card, backdrop click and Escape close), but read-only and list-only: no
 * input, no tabs. Steps are concise lines, not prose.
 *
 * For a job the new job-monitoring system knows (monitor === 'new'), the modal
 * also shows the job's recent run history from ops_job_run (started, outcome,
 * duration, records handled) with the full error text under each failed run.
 * This is the expansion for the grid's one-line truncated error.
 */

import { useEffect } from 'react';
import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import {
  formatDurationMs,
  formatLocalTime,
  monitorFacts,
  useCockpitOpsJobRuns,
  type RegistryJob,
} from '@/lib/useCockpitOps';
import { Pill } from '@/components/Pill';

/** Recent run history section, only rendered for new-monitored jobs. */
function RunHistory({ job }: { job: RegistryJob }) {
  const { runs, isLoading, error } = useCockpitOpsJobRuns(job.job_id);
  if (isLoading) {
    return (
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" />
        Loading run history...
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-red-700 dark:text-red-300">
        Could not load run history: {error.message}
      </p>
    );
  }
  if (runs.length === 0) {
    return (
      <p className="text-sm italic text-amp-ink-muted dark:text-amp-dark-ink-muted">
        No runs recorded yet.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {runs.map((run) => {
        const inFlight = run.finished_at === null;
        return (
          <li key={run.run_id} className="text-xs">
            <div className="flex flex-wrap items-center gap-2">
              {run.outcome === 'success' ? (
                <Pill tone="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                  <CheckCircle2 className="h-3 w-3" /> success
                </Pill>
              ) : run.outcome === 'failure' ? (
                <Pill tone="bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                  <XCircle className="h-3 w-3" /> failure
                </Pill>
              ) : (
                <Pill tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
                  {inFlight ? 'running' : 'unknown'}
                </Pill>
              )}
              <span className="text-amp-ink dark:text-amp-dark-ink">
                {formatLocalTime(run.started_at)}
              </span>
              <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted tabular-nums">
                {formatDurationMs(run.duration_ms)}
              </span>
              {run.records_handled !== null && (
                <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted tabular-nums">
                  {run.records_handled.toLocaleString()} records
                </span>
              )}
              <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                on {run.machine}
              </span>
            </div>
            {run.error_text && (
              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-red-50 p-2 font-mono text-[11px] text-red-800 dark:bg-red-900/20 dark:text-red-300">
                {run.error_text}
              </pre>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function JobStepsModal({
  job,
  onClose,
}: {
  job: RegistryJob;
  onClose: () => void;
}) {
  // Escape closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${job.job_id} logic`}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl rounded-lg border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        <div className="border-b border-amp-line px-5 pt-5 pb-3 dark:border-amp-dark-line">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Job logic
          </p>
          <h2 className="font-mono text-lg font-semibold text-amp-ink dark:text-amp-dark-ink break-all">
            {job.job_id}
          </h2>
          {job.short_description && (
            <p className="mt-1 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
              {job.short_description}
            </p>
          )}
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-5">
          {job.detail_steps.length === 0 ? (
            <p className="text-sm italic text-amp-ink-muted dark:text-amp-dark-ink-muted">
              No step list on file for this job yet.
            </p>
          ) : (
            <ol className="list-decimal space-y-2 pl-5 text-sm text-amp-ink dark:text-amp-dark-ink">
              {job.detail_steps.map((step, i) => (
                <li key={i} className="pl-1">
                  {step}
                </li>
              ))}
            </ol>
          )}

          {monitorFacts(job).isNew && (
            <div className="mt-5 border-t border-amp-line pt-4 dark:border-amp-dark-line">
              <p className="mb-2 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
                Recent runs
              </p>
              <RunHistory job={job} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
